import type { PhoneVerificationResult } from "@/app/actions/verification";

/**
 * sessionStorage key for the phone number on the native identity bridge.
 *
 * verify-phone-external sets it before opening PortOne; when Safari blocks
 * the popup and the flow continues as a full-page redirect, the bridge
 * callback reads it back, since the redirect URL carries only PortOne's own
 * query (LOCARORA-WEB-5B).
 */
export const IDENTITY_BRIDGE_PHONE_KEY = "locarora.identityBridgePhone";

/**
 * 앱 본인인증을 시작한 화면과 복귀 deep link 를 잇는 연결.
 *
 * 인증 창은 앱 위에 떠 있을 뿐 시작 화면(결제 등)은 그대로 살아 있으므로, 결과를 그
 * 화면에 직접 전달해 입력값을 잃지 않게 한다. 화면이 사라졌으면(리로드 등) 핸들러가
 * 없으므로 저장해 둔 경로로 이동한다.
 */
export interface IdentityResultHandler {
  /** 복귀 링크 도착, 서버 확인 시작. */
  onProcessing: () => void;
  onVerified: (result: PhoneVerificationResult) => void;
  onFailed: (code: string) => void;
  onCancelled: () => void;
}

let activeHandler: IdentityResultHandler | null = null;

export function setIdentityResultHandler(handler: IdentityResultHandler): void {
  activeHandler = handler;
}

/** 이 핸들러가 아직 등록돼 있을 때만 해제한다 (더 새 시도를 지우지 않게). */
export function clearIdentityResultHandler(handler: IdentityResultHandler | null): void {
  if (handler && activeHandler === handler) activeHandler = null;
}

/** 결과는 한 번만 전달되도록 꺼내면서 비운다. */
export function takeIdentityResultHandler(): IdentityResultHandler | null {
  const handler = activeHandler;
  activeHandler = null;
  return handler;
}

const IDENTITY_RETURN_PATH_KEY = "locarora.identityReturnPath";

export function rememberIdentityReturnPath(path: string): void {
  try {
    sessionStorage.setItem(IDENTITY_RETURN_PATH_KEY, path);
  } catch {
    // Storage unavailable — falls back to home.
  }
}

/** 같은 사이트 안의 경로만 돌려준다. 저장값은 꺼내면서 지운다. */
export function consumeIdentityReturnPath(): string | null {
  try {
    const path = sessionStorage.getItem(IDENTITY_RETURN_PATH_KEY);
    sessionStorage.removeItem(IDENTITY_RETURN_PATH_KEY);
    if (!path || !path.startsWith("/") || path.startsWith("//")) return null;
    return path;
  } catch {
    return null;
  }
}
